import * as React from "react";
import {
  ChevronDown,
  ChevronRight,
  AlertCircle,
  CornerDownRight,
  Loader2,
  MessageSquarePlus,
  RefreshCw,
  SendHorizontal,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { aggregateDdqSources, type ChatSource, type DDQFollowup } from "@/lib/api";
import type { DDQAnsweredQuestion, DDQParsedQuestions } from "@/lib/api";
import { buildCitationMap, renderMarkdown } from "@/lib/markdown";
import { FeedbackWidget, isServerMessageId } from "@/components/chat/FeedbackWidget";
import { buildDdqSections, isPending } from "./ddqOrder";
import type { SectionItem } from "./ddqOrder";

/**
 * Renders a DDQ response payload as a collapsible accordion: one panel
 * per section (expanded by default), Q&A blocks collapsed by default so
 * the LP can scan section → question → answer. Each answer carries its
 * citation chips (rendered via the same markdown pipeline as standard
 * chat, so citation tags resolve to source-document chips and clicks
 * surface the source card just like in normal chat).
 *
 * When `parsedStructure` is provided, sections and questions render in
 * the LP's original DDQ order — useful during streaming so the UI does
 * not re-shuffle as answers arrive out of order from the parallel
 * generation pool. Without it, we fall back to arrival order (which
 * matches what the server stamps into the persisted message anyway).
 */
export function DDQAccordionRenderer({
  questions,
  onCitationClick,
  onAskFollowup,
  onFollowup,
  onRerunQuestion,
  showTransient = false,
  parsedStructure,
  messageId,
}: {
  questions: DDQAnsweredQuestion[];
  onCitationClick?: (source: ChatSource) => void;
  /** Legacy handler: primes a follow-up question in the bottom chat input.
   *  Kept as the fallback when the inline flow (`onFollowup`) isn't wired. */
  onAskFollowup?: (q: DDQAnsweredQuestion) => void;
  /** Inline follow-up (#17): submits the LP's typed question through the
   *  existing /v1/chat/sync path (with light DDQ context prefixing) and
   *  resolves to the answer + sources rendered inline under the block.
   *  When present this supersedes the legacy bottom-input prime. */
  onFollowup?: (q: DDQAnsweredQuestion, text: string) => Promise<DDQFollowup>;
  /** Per-question re-run (#14): re-executes one question server-side and
   *  resolves once the parent has swapped the fresh answer into state. */
  onRerunQuestion?: (q: DDQAnsweredQuestion) => Promise<DDQAnsweredQuestion>;
  /** If true, dim the most recently appended question to hint at streaming. */
  showTransient?: boolean;
  /** Canonical DDQ structure used to keep the live view in parsed order. */
  parsedStructure?: DDQParsedQuestions | null;
  /** Server message id for the whole DDQ response — enables per-answer
   *  feedback. Absent during live streaming (the persisted id arrives on
   *  reload), so the feedback widget renders only when this is a UUID. */
  messageId?: string;
}) {
  // Session-local follow-ups (#17 v1), keyed by question_id. Held here (not
  // in AnswerBlock) so they survive collapse/expand — the AnswerBlock
  // unmounts when its question is collapsed.
  const [followupsByQuestion, setFollowupsByQuestion] = React.useState<
    Record<string, DDQFollowup[]>
  >({});
  const appendFollowup = React.useCallback((questionId: string, f: DDQFollowup) => {
    setFollowupsByQuestion((prev) => ({
      ...prev,
      [questionId]: [...(prev[questionId] || []), f],
    }));
  }, []);
  // Group questions by section — delegates to the shared ddqOrder helper so the
  // accordion and the Workroom ledger use ONE ordering source. Behaviour is
  // byte-identical to the previous inline logic.
  const sections = React.useMemo(
    () => buildDdqSections(questions, parsedStructure, showTransient),
    [questions, parsedStructure, showTransient],
  );

  // Global, deduped citation numbering across ALL questions — the SAME list
  // (and helper) the Sources panel uses. Each answer's inline chips are
  // numbered from this so a chip's number matches its source card (and a
  // repeated citation reuses its number instead of double-counting).
  const aggregatedSources = React.useMemo(() => aggregateDdqSources(questions), [questions]);

  // Section expansion state — defaults to all expanded; LP can collapse.
  const [collapsedSections, setCollapsedSections] = React.useState<Set<string>>(new Set());
  // Question expansion — defaults to all collapsed; LP can expand.
  const [openQs, setOpenQs] = React.useState<Set<string>>(new Set());

  const toggleSection = (name: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };
  const toggleQuestion = (id: string) => {
    setOpenQs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-3">
      {sections.map((section) => {
        const collapsed = collapsedSections.has(section.name);
        const answeredItems = section.items.filter((q) => !isPending(q)) as DDQAnsweredQuestion[];
        const pendingCount = section.items.length - answeredItems.length;
        const followups = answeredItems.filter((q) => q.needs_gp_followup).length;
        return (
          <section key={section.name} className="overflow-hidden rounded-md border bg-card shadow-sm">
            <button
              type="button"
              onClick={() => toggleSection(section.name)}
              className="flex w-full items-center justify-between gap-2 border-b bg-secondary/50 px-4 py-2 text-left text-sm font-semibold hover:bg-secondary"
              aria-expanded={!collapsed}
            >
              <span className="flex items-center gap-2">
                {collapsed ? (
                  <ChevronRight className="size-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="size-4 text-muted-foreground" />
                )}
                {section.name}
                <Badge variant="outline" className="font-mono text-[10px]">
                  {pendingCount > 0
                    ? `${answeredItems.length}/${section.items.length}`
                    : section.items.length}
                </Badge>
                {followups > 0 ? (
                  <Badge
                    variant="outline"
                    className="border-warning/50 bg-warning-soft text-[10px] text-warning"
                  >
                    {followups} need GP follow-up
                  </Badge>
                ) : null}
              </span>
            </button>

            {!collapsed ? (
              <ul className="divide-y">
                {section.items.map((q) => {
                  if (isPending(q)) {
                    return (
                      <li key={q.question_id}>
                        <div className="flex w-full items-start gap-2 px-4 py-3 text-left text-sm opacity-70">
                          <Loader2 className="mt-1 size-4 shrink-0 animate-spin text-muted-foreground" />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start gap-2">
                              <span className="font-mono text-xs text-muted-foreground">
                                {q.question_id}.
                              </span>
                              <span className="font-medium text-muted-foreground">
                                {q.question}
                              </span>
                            </div>
                            <Badge
                              variant="outline"
                              className="mt-1.5 px-1.5 py-0 font-mono text-[9px] uppercase tracking-wider text-muted-foreground"
                            >
                              Pending
                            </Badge>
                          </div>
                        </div>
                      </li>
                    );
                  }
                  const isOpen = openQs.has(q.question_id);
                  return (
                    <li key={q.question_id}>
                      <button
                        type="button"
                        onClick={() => toggleQuestion(q.question_id)}
                        className="flex w-full items-start gap-2 px-4 py-3 text-left text-sm hover:bg-secondary/40"
                        aria-expanded={isOpen}
                      >
                        {isOpen ? (
                          <ChevronDown className="mt-1 size-4 shrink-0 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="mt-1 size-4 shrink-0 text-muted-foreground" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start gap-2">
                            <span className="font-mono text-xs text-muted-foreground">
                              {q.question_id}.
                            </span>
                            <span className="font-medium">{q.question}</span>
                          </div>
                          {q.needs_gp_followup ? (
                            <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-warning">
                              <AlertCircle className="size-3" />
                              Not addressed in the data room — GP will respond
                              directly
                            </span>
                          ) : null}
                        </div>
                      </button>
                      {isOpen ? (
                        <AnswerBlock
                          q={q}
                          aggregatedSources={aggregatedSources}
                          onCitationClick={onCitationClick}
                          onAskFollowup={onAskFollowup}
                          onFollowup={onFollowup}
                          onRerunQuestion={onRerunQuestion}
                          followups={followupsByQuestion[q.question_id] || []}
                          onFollowupSaved={appendFollowup}
                          messageId={messageId}
                        />
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}

function AnswerBlock({
  q,
  aggregatedSources,
  onCitationClick,
  onAskFollowup,
  onFollowup,
  onRerunQuestion,
  followups = [],
  onFollowupSaved,
  messageId,
}: {
  q: DDQAnsweredQuestion;
  /** Globally-deduped, globally-numbered sources for the whole DDQ — the same
   *  list the Sources panel shows. Chips are numbered + resolved from this so
   *  a chip's number matches its source card. Falls back to the question's own
   *  citations only if absent. */
  aggregatedSources?: ChatSource[];
  onCitationClick?: (source: ChatSource) => void;
  onAskFollowup?: (q: DDQAnsweredQuestion) => void;
  onFollowup?: (q: DDQAnsweredQuestion, text: string) => Promise<DDQFollowup>;
  onRerunQuestion?: (q: DDQAnsweredQuestion) => Promise<DDQAnsweredQuestion>;
  followups?: DDQFollowup[];
  onFollowupSaved?: (questionId: string, f: DDQFollowup) => void;
  messageId?: string;
}) {
  const sources =
    aggregatedSources && aggregatedSources.length ? aggregatedSources : q.citations || [];
  const citationMap = React.useMemo(() => buildCitationMap(sources), [sources]);
  const html = React.useMemo(
    () => renderMarkdown(q.answer, citationMap),
    [q.answer, citationMap],
  );

  // Inline follow-up composer state (#17).
  const [composerOpen, setComposerOpen] = React.useState(false);
  const [followupText, setFollowupText] = React.useState("");
  const [followupBusy, setFollowupBusy] = React.useState(false);
  const [followupError, setFollowupError] = React.useState<string | null>(null);

  // Per-question re-run state (#14). The fresh answer arrives via the parent
  // swapping `q` in state, so this block only tracks the in-flight UI.
  const [rerunBusy, setRerunBusy] = React.useState(false);
  const [rerunError, setRerunError] = React.useState<string | null>(null);

  // Delegate citation clicks. The chip's data-source carries the GLOBAL source
  // index (1-based), matching the Sources panel; resolve it to that source.
  const onBodyClick = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = (e.target as HTMLElement).closest<HTMLElement>("[data-source]");
      if (!target) return;
      const idx = Number(target.dataset.source);
      if (!Number.isFinite(idx)) return;
      const source = sources.find((s) => s.index === idx) || sources[idx - 1];
      if (source && onCitationClick) onCitationClick(source);
    },
    [sources, onCitationClick],
  );

  const submitFollowup = React.useCallback(async () => {
    const text = followupText.trim();
    if (!text || followupBusy || !onFollowup) return;
    setFollowupBusy(true);
    setFollowupError(null);
    try {
      const f = await onFollowup(q, text);
      onFollowupSaved?.(q.question_id, f);
      setFollowupText("");
      setComposerOpen(false);
    } catch (err) {
      setFollowupError(err instanceof Error ? err.message : "Follow-up failed");
    } finally {
      setFollowupBusy(false);
    }
  }, [followupText, followupBusy, onFollowup, onFollowupSaved, q]);

  const submitRerun = React.useCallback(async () => {
    if (rerunBusy || !onRerunQuestion) return;
    setRerunBusy(true);
    setRerunError(null);
    try {
      await onRerunQuestion(q);
    } catch (err) {
      setRerunError(err instanceof Error ? err.message : "Re-run failed");
    } finally {
      setRerunBusy(false);
    }
  }, [rerunBusy, onRerunQuestion, q]);

  return (
    <div className="px-9 pb-4 pt-1">
      <div
        className={rerunBusy ? "prose-chat opacity-50" : "prose-chat"}
        onClick={onBodyClick}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {q.sub_questions && q.sub_questions.length > 0 ? (
        <div className="mt-2 text-[11px] text-muted-foreground">
          Sub-parts asked: {q.sub_questions.join(" · ")}
        </div>
      ) : null}

      {q.rerun ? (
        <div className="mt-2 text-[11px] text-muted-foreground">
          Re-run {q.rerun.count}× · last{" "}
          {new Date(q.rerun.last_at).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          })}
        </div>
      ) : null}

      {/* Inline follow-ups (#17): each renders as an indented Q/A pair
          beneath the answer it was asked about, with its own citation map. */}
      {followups.length > 0 ? (
        <div className="mt-3 space-y-3 border-l-2 border-border pl-3">
          {followups.map((f, i) => (
            <FollowupItem key={`${q.question_id}-fu-${i}`} followup={f} onCitationClick={onCitationClick} />
          ))}
        </div>
      ) : null}

      {(rerunError || followupError) ? (
        <div className="mt-2 rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive">
          {rerunError || followupError}
        </div>
      ) : null}

      {composerOpen && onFollowup ? (
        <div className="mt-3 rounded-md border bg-background p-2">
          <div className="flex items-end gap-2">
            <Textarea
              value={followupText}
              onChange={(e) => setFollowupText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void submitFollowup();
                }
              }}
              placeholder={`Ask a follow-up about Q${q.question_id}…`}
              aria-label={`Follow-up question about ${q.question_id}`}
              className="min-h-10 resize-none border-0 shadow-none focus-visible:ring-0"
              disabled={followupBusy}
            />
            <Button
              type="button"
              size="icon"
              aria-label="Send follow-up"
              onClick={() => void submitFollowup()}
              disabled={!followupText.trim() || followupBusy}
            >
              {followupBusy ? <Loader2 className="animate-spin" /> : <SendHorizontal />}
            </Button>
          </div>
          <div className="mt-1 flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground">
              Answered from the data room via the standard chat path.
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setComposerOpen(false);
                setFollowupError(null);
              }}
              disabled={followupBusy}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      <div className="mt-3 flex items-center justify-end gap-2">
        {onRerunQuestion ? (
          <Button
            type="button"
            variant="subtle"
            size="sm"
            onClick={() => void submitRerun()}
            disabled={rerunBusy}
            aria-label={`Re-run question ${q.question_id}`}
          >
            <RefreshCw className={rerunBusy ? "animate-spin" : undefined} />
            {rerunBusy ? "Re-running…" : "Re-run"}
          </Button>
        ) : null}
        {onFollowup && !composerOpen ? (
          <Button
            type="button"
            variant="subtle"
            size="sm"
            onClick={() => setComposerOpen(true)}
            aria-label={`Ask a follow-up question about ${q.question_id}`}
          >
            <MessageSquarePlus />
            Ask follow-up
          </Button>
        ) : null}
        {!onFollowup && onAskFollowup ? (
          // Legacy fallback: prime the bottom chat input (pre-#17 behavior).
          <Button
            type="button"
            variant="subtle"
            size="sm"
            onClick={() => onAskFollowup(q)}
            aria-label={`Ask a follow-up question about ${q.question_id}`}
          >
            <MessageSquarePlus />
            Ask follow-up
          </Button>
        ) : null}
      </div>
      {isServerMessageId(messageId) ? (
        <div className="mt-3 border-t pt-2">
          <FeedbackWidget
            messageId={messageId as string}
            context={`Q${q.question_id} · ${q.section}`}
          />
        </div>
      ) : null}
    </div>
  );
}

// One inline follow-up Q/A pair. The answer renders through the SAME markdown
// + citation-chip pipeline as standard chat; chips resolve within the
// follow-up's own sources (already renumbered against the global panel by the
// parent page) and surface the source card via onCitationClick.
function FollowupItem({
  followup,
  onCitationClick,
}: {
  followup: DDQFollowup;
  onCitationClick?: (source: ChatSource) => void;
}) {
  const citationMap = React.useMemo(
    () => buildCitationMap(followup.sources),
    [followup.sources],
  );
  const html = React.useMemo(
    () => renderMarkdown(followup.answer, citationMap),
    [followup.answer, citationMap],
  );
  const onBodyClick = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = (e.target as HTMLElement).closest<HTMLElement>("[data-source]");
      if (!target) return;
      const idx = Number(target.dataset.source);
      if (!Number.isFinite(idx)) return;
      const source =
        followup.sources.find((s) => s.index === idx) || followup.sources[idx - 1];
      if (source && onCitationClick) onCitationClick(source);
    },
    [followup.sources, onCitationClick],
  );
  return (
    <div data-testid="ddq-followup">
      <div className="flex items-start gap-1.5 text-xs font-medium">
        <CornerDownRight className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
        <span>{followup.question}</span>
      </div>
      <div
        className="prose-chat mt-1.5 pl-5 text-sm"
        onClick={onBodyClick}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}
